/**
 * useProductChannelList — Derives the canonical channel list for a product
 * from `getProductAssets` (grouped_by_channel + channel_meta), applying the
 * same Wawi-filter as ProductImagesSubTab so IDs are always `channels.id` UUIDs.
 *
 * Contract:
 * - non-global channels: id = grouped_by_channel key (UUID)
 * - global/"Alle Kanäle": id = channel_meta.global.channel_uuid
 * - Wawi channels (1-1-0, 3-*, 4-* jtl_id prefixes) are excluded
 * - Name comes from channel_meta[key].channel_name (same source as ProductImagesSubTab)
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getProductAssets } from '@/lib/api/products'
import type { ApplyChannel } from '@/components/v2/tools/amazon-listing/RemakeApply/RemakeApplyChannelPicker'

function isWawiChannel(jtlId: string): boolean {
  const id = jtlId.toLowerCase()
  return id === '1-1-0' || id.startsWith('3-') || id.startsWith('4-')
}

export function useProductChannelList(productId: string): ApplyChannel[] {
  const { data } = useQuery({
    queryKey: ['product-assets', productId],
    queryFn: () => getProductAssets(productId),
    staleTime: 30_000,
    enabled: !!productId,
  })

  return useMemo(() => {
    const grouped = data?.grouped_by_channel ?? {}
    const meta = data?.channel_meta ?? {}

    const sortedKeys = Object.keys(grouped)
      .filter((key) => {
        if (key === 'global') return true
        const m = meta[key]
        if (m && isWawiChannel(m.channel_id_jtl ?? '')) return false
        const ch = grouped[key]
        if (ch && ch.length > 0) {
          const ct = typeof ch[0].channel_type === 'string'
            ? ch[0].channel_type.toLowerCase()
            : ''
          if (ct.includes('wawi')) return false
        }
        return true
      })
      .sort((a, b) => (a === 'global' ? -1 : b === 'global' ? 1 : a.localeCompare(b)))

    return sortedKeys.map((key): ApplyChannel => {
      const isGlobal = key === 'global'
      const m = meta[key]
      const globalMeta = meta['global']
      const id = isGlobal ? (globalMeta?.channel_uuid ?? key) : key
      const name = m?.channel_name ?? key
      // Readable identifier — only for non-global channels with a JTL id.
      const externalChannelId = !isGlobal && m?.channel_id_jtl
        ? m.channel_id_jtl
        : undefined
      return {
        id,
        name,
        isMaster: isGlobal,
        jtlSynced: true,
        hasBulletMapping: false,
        externalChannelId,
      }
    })
  }, [data])
}
